import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMemberById } from '../firebase/database';
import PaymentForm from './PaymentForm';
import MembershipForm from './MembershipForm';
import PauseHistoryBadge from './PauseHistoryBadge';
import MemberActivityLog from './MemberActivityLog';
import DeleteMemberDialog from './DeleteMemberDialog';
import WeightTracker from './WeightTracker';
import {
  calculateMembershipFees,
  formatDate,
  getEffectiveRegistrationDate,
  getOriginalRegistrationDate,
  isMembershipPaused,
  startOfDay,
  summarizePauseHistory,
  toDate
} from '../utils/membershipFees';
import './MemberDetails.css';

const MemberDetails = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId } = useAuth();
  const navigationType = useNavigationType();
  const historyShimAppliedRef = useRef(false);
  const [member, setMember] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states for forms
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  
  // New modal states for membership management
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // Bumped after any change so the activity log reloads
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const [pauseFormData, setPauseFormData] = useState({
    reason: '',
    pauseDate: new Date().toISOString().split('T')[0]
  });
  const [resumeFormData, setResumeFormData] = useState({
    resumeDate: new Date().toISOString().split('T')[0]
  });
  
  // Notification states
  const [pauseNotification, setPauseNotification] = useState({ show: false, type: '', message: '' });
  const [resumeNotification, setResumeNotification] = useState({ show: false, type: '', message: '' });

  console.log('MemberDetails hooks called:', { memberId, member, isLoading, error });

  // Fetch member data when component mounts
  useEffect(() => {
    if (!tenantId || !memberId) return;
    const fetchMemberData = async () => {
      try {
        setIsLoading(true);
        const result = await getMemberById(tenantId, memberId);
        console.log('getMemberById result:', result);
        if (result.success && result.member) {
          console.log('Complete member object:', result.member);
          console.log('Member object keys:', Object.keys(result.member));
          console.log('Member fees field:', result.member.fees);
          console.log('Member totalPaid field:', result.member.totalPaid);
          console.log('Member fees type:', typeof result.member.fees);
          console.log('Member totalPaid type:', typeof result.member.totalPaid);
          setMember(result.member);
        } else {
          setError(result.message || 'Member not found');
        }
      } catch (err) {
        console.error('Error fetching member:', err);
        setError('Failed to load member data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMemberData();
  }, [tenantId, memberId]);

  // Load payment history (you can implement this with your Firebase functions)
  useEffect(() => {
    console.log('MemberDetails useEffect for payment history:', { member, memberIdForQuery: member?.id });
    if (member && member.id) {
      loadPaymentHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.id]);

  // Load payment history to calculate total paid
  const loadPaymentHistory = async () => {
    if (!tenantId || !member?.id) return;
    try {
      console.log('Member ID being used for payment query:', member.id);
      console.log('Member object keys:', Object.keys(member));
      
      // Import the function dynamically to avoid circular dependencies
      const { getMemberPayments, getAllPayments } = await import('../firebase/database');
      console.log('getMemberPayments function imported successfully');
      
      const result = await getMemberPayments(tenantId, member.id);
      console.log('getMemberPayments result:', result);
      
      if (result.success) {
        console.log('Payment history loaded successfully:', result.payments);
        console.log('Number of payments found:', result.payments.length);
        setPaymentHistory(result.payments);
        
        // Calculate total paid from actual payments
        const totalPaidFromPayments = result.payments.reduce((total, payment) => {
          return total + (payment.amount || 0);
        }, 0);
        
        console.log('Total paid calculated from payments:', totalPaidFromPayments);
        
        // Update the member object with the calculated total paid
        if (totalPaidFromPayments !== member.totalPaid) {
          setMember(prev => ({
            ...prev,
            totalPaid: totalPaidFromPayments
          }));
        }
      } else {
        console.log('Failed to load payment history via indexed query. Falling back to full scan...', result.message);
        // Fallback: load all payments and filter by memberId
        const all = await getAllPayments(tenantId);
        if (all.success) {
          const memberPayments = all.payments.filter(p => p.memberId === member.id);
          console.log('Fallback member payments found:', memberPayments.length);
          setPaymentHistory(memberPayments);
        } else {
          console.log('Fallback getAllPayments failed:', all.message);
        }
      }
    } catch (err) {
      console.error('Error loading payment history:', err);
    }
  };

  // Scroll to top on normal navigations, but preserve position on browser back/forward
  useEffect(() => {
    if (navigationType === 'POP') {
      return; // preserve scroll/state on browser back/forward
    }
    const resetScroll = () => {
      window.scrollTo(0, 0);
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      if (document.body) document.body.scrollTop = 0;
      if (document.documentElement) document.documentElement.scrollTop = 0;
      const scrollableElements = document.querySelectorAll('.member-details-container, .dashboard-content, [data-scrollable]');
      scrollableElements.forEach(element => {
        if (element && element.scrollTop !== undefined) {
          element.scrollTop = 0;
        }
      });
    };
    resetScroll();
    setTimeout(resetScroll, 100);
    setTimeout(resetScroll, 300);
  }, [navigationType]);

  // Ensure browser back goes to members list (insert '/dashboard' as previous entry)
  useEffect(() => {
    if (navigationType === 'POP') return; // don't modify history on back/forward
    if (historyShimAppliedRef.current) return;
    try {
      const current = window.location.pathname + window.location.search + window.location.hash;
      const fromDashboard = document.referrer && new URL(document.referrer).pathname.includes('/dashboard');
      if (!fromDashboard) {
        window.history.replaceState(null, '', '/dashboard');
        window.history.pushState(null, '', current);
      }
      historyShimAppliedRef.current = true;
    } catch (_) {
      // ignore
    }
  }, [navigationType]);

  // Keyboard event handler for closing modals with Escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (showPaymentForm) {
          closePaymentForm();
        }
      }
    };

    // Add event listener if any modal is open
    if (showPaymentForm) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showPaymentForm]);

  // Modal close handlers
  const closePaymentForm = () => {
    console.log('Closing payment form modal');
    setShowPaymentForm(false);
  };

  
  // Debug modal states
  useEffect(() => {
    console.log('Modal states changed:', {
      showPaymentForm,
      
    });
  }, [showPaymentForm]);

  // Navigation handlers (removed back button; rely on browser history)

  const handleRecordPayment = (member) => {
    console.log('Record Payment clicked for member:', member);
    setShowPaymentForm(true);
  };

  

  // Show loading state
  if (isLoading) {
    return (
      <div className="member-details-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading member details...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !member) {
    return (
      <div className="member-details-container">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error || 'Member not found'}</p>
        </div>
      </div>
    );
  }

  // Calculate payment statistics - moved here to ensure member exists
  const parseFee = (feeValue) => {
    if (typeof feeValue === 'number') return feeValue;
    if (typeof feeValue === 'string') {
      // Remove currency symbols and commas, then parse as number
      const cleaned = feeValue.replace(/[₹$,]/g, '').trim();
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  // Use payment history to calculate total paid if available
  const totalPaidFromPayments = paymentHistory.reduce((total, payment) => {
    return total + (payment.amount || 0);
  }, 0);

  const totalFee = parseFee(member.fees);
  const totalPaid = totalPaidFromPayments > 0 ? totalPaidFromPayments : parseFee(member.totalPaid);

  // Fee accrual stops on the pause date and restarts from the resume date
  const feeSummary = calculateMembershipFees(member, { totalPaid });
  const totalCharged = feeSummary.totalCharged;
  const balanceDue = feeSummary.balanceDue;
  const isFullyPaid = feeSummary.isFullyPaid;
  const paused = isMembershipPaused(member);
  const pauseSummary = summarizePauseHistory(member);
  const originalRegistrationDate = getOriginalRegistrationDate(member);
  const effectiveRegistrationDate = getEffectiveRegistrationDate(member);

  // Most recent resume, which becomes the member's new (effective) registration date
  const lastResumeDate =
    member.resumeDate ||
    member.lastResumeDate ||
    [...pauseSummary.history].reverse().find((entry) => entry.resumeDate)?.resumeDate ||
    null;

  // Compare by calendar day so mixed date formats don't read as a change
  const billingAnchorMoved =
    Boolean(lastResumeDate) ||
    Boolean(
      effectiveRegistrationDate &&
        originalRegistrationDate &&
        startOfDay(effectiveRegistrationDate)?.getTime() !==
          startOfDay(originalRegistrationDate)?.getTime()
    );

  console.log('Fee accrual summary:', feeSummary);

  // Refresh member data after payment
  const refreshMemberData = async () => {
    if (!tenantId) return;
    try {
      const result = await getMemberById(tenantId, memberId);
      if (result.success && result.member) {
        console.log('Refreshed member data:', result.member);
        setMember(result.member);
        setActivityRefreshKey((key) => key + 1);

        // Also refresh payment history after member data refresh
        setTimeout(() => {
          loadPaymentHistory();
        }, 500);
      }
    } catch (err) {
      console.error('Error refreshing member data:', err);
    }
  };

  // Manual refresh payment history
  // eslint-disable-next-line no-unused-vars
  const manualRefreshPayments = async () => {
    try {
      console.log('Manually refreshing payment history...');
      await loadPaymentHistory();
    } catch (err) {
      console.error('Error manually refreshing payments:', err);
    }
  };

  // Fallback: Get all payments and filter manually
  // eslint-disable-next-line no-unused-vars
  const loadAllPaymentsAndFilter = async () => {
    if (!tenantId) return;
    try {
      const { getAllPayments } = await import('../firebase/database');
      const result = await getAllPayments(tenantId);
      
      if (result.success) {
        console.log('All payments loaded:', result.payments);
        
        // Filter payments for this member
        const memberPayments = result.payments.filter(payment => {
          console.log('Checking payment:', payment.memberId, 'against member ID:', member.id);
          return payment.memberId === member.id;
        });
        
        console.log('Filtered payments for this member:', memberPayments);
        setPaymentHistory(memberPayments);
        
        // Calculate total paid
        const totalPaidFromPayments = memberPayments.reduce((total, payment) => {
          return total + (payment.amount || 0);
        }, 0);
        
        console.log('Total paid from filtered payments:', totalPaidFromPayments);
      } else {
        console.log('Failed to load all payments:', result.message);
      }
    } catch (err) {
      console.error('Error in fallback payment loading:', err);
    }
  };

  // Debug: Check raw payments data structure
  // eslint-disable-next-line no-unused-vars
  const debugPaymentsStructure = async () => {
    if (!tenantId) return;
    try {
      const { getAllPayments } = await import('../firebase/database');
      const result = await getAllPayments(tenantId);
      
      if (result.success && result.payments.length > 0) {
        console.log('First payment object structure:', result.payments[0]);
        console.log('First payment keys:', Object.keys(result.payments[0]));
        console.log('All payments memberId values:', result.payments.map(p => p.memberId));
        
        // Check if any payments match our member
        const matchingPayments = result.payments.filter(p => p.memberId === member.id);
        console.log('Payments matching member ID:', matchingPayments);
        
        if (matchingPayments.length === 0) {
          console.log('⚠️ No payments found with memberId:', member.id);
          console.log('Available memberIds in payments:', [...new Set(result.payments.map(p => p.memberId))]);
        }
      } else {
        console.log('No payments found in database');
      }
    } catch (err) {
      console.error('Error debugging payments structure:', err);
    }
  };

  // Recalculate member balances
  // eslint-disable-next-line no-unused-vars
  const recalculateBalances = async () => {
    try {
      console.log('🔄 Recalculating balances for member:', member.id);
      const { recalculateMemberBalances } = await import('../firebase/database');
      const result = await recalculateMemberBalances(tenantId, member.id);
      
      if (result.success) {
        console.log('Balances recalculated successfully:', result.balance);
        // Refresh member data to show updated balances
        await refreshMemberData();
      } else {
        console.log('Failed to recalculate balances:', result.message);
      }
    } catch (err) {
      console.error('Error recalculating balances:', err);
    }
  };

  // Get member balance info
  // eslint-disable-next-line no-unused-vars
  const getBalanceInfo = async () => {
    try {
      console.log('💰 Getting balance info for member:', member.id);
      const { getMemberBalance } = await import('../firebase/database');
      const result = await getMemberBalance(member.id);
      
      if (result.success) {
        console.log('Member balance info:', result.balance);
      } else {
        console.log('Failed to get balance info:', result.message);
      }
    } catch (err) {
      console.error('Error getting balance info:', err);
    }
  };

  // Get comprehensive billing information
  // eslint-disable-next-line no-unused-vars
  const getBillingInfo = async () => {
    try {
      console.log('📊 Getting comprehensive billing info for member:', member.id);
      const { getMemberBillingInfo } = await import('../firebase/database');
      const result = await getMemberBillingInfo(member.id);
      
      if (result.success) {
        console.log('Comprehensive billing info:', result.billing);
      } else {
        console.log('Failed to get billing info:', result.message);
      }
    } catch (err) {
      console.error('Error getting billing info:', err);
    }
  };

  // Custom confirmation function to avoid ESLint warnings
  // eslint-disable-next-line no-unused-vars
  const showConfirmation = (message) => {
    return window.confirm(message);
  };

  // Pause membership
  const pauseMembership = async () => {
    try {
      // Validate form data
      if (!pauseFormData.reason.trim()) {
        setPauseNotification({
          show: true,
          type: 'error',
          message: 'Please enter a reason for pausing membership.'
        });
        return;
      }
      
      if (!pauseFormData.pauseDate) {
        setPauseNotification({
          show: true,
          type: 'error',
          message: 'Please select a pause date.'
        });
        return;
      }
      
      // Validate date format
      const pauseDate = new Date(pauseFormData.pauseDate);
      if (isNaN(pauseDate.getTime())) {
        setPauseNotification({
          show: true,
          type: 'error',
          message: 'Invalid date format. Please select a valid date.'
        });
        return;
      }
      
      console.log('⏸️ Pausing membership for member:', member.id, 'from date:', pauseDate);
      
      const { pauseMembership: pauseMembershipFn } = await import('../firebase/database');
      const result = await pauseMembershipFn(tenantId, member.id, pauseFormData.reason, pauseDate.toISOString());
      
      if (result.success) {
        console.log('Membership paused successfully:', result.pause);
        
        // Show success notification
        setPauseNotification({
          show: true,
          type: 'success',
          message: `Membership paused successfully! Reason: ${pauseFormData.reason}, Pause Date: ${pauseDate.toLocaleDateString()}, Pause Month: ${result.pause.month}. No fees will be charged while paused.`
        });
        
        // Reset form and close modal after delay
        setTimeout(() => {
          setPauseFormData({
            reason: '',
            pauseDate: new Date().toISOString().split('T')[0]
          });
          setShowPauseModal(false);
          setPauseNotification({ show: false, type: '', message: '' });
        }, 3000);
        
        // Refresh member data to show updated status
        await refreshMemberData();
      } else {
        console.log('Failed to pause membership:', result.message);
        setPauseNotification({
          show: true,
          type: 'error',
          message: `Failed to pause membership: ${result.message}`
        });
      }
    } catch (err) {
      console.error('Error pausing membership:', err);
      setPauseNotification({
        show: true,
        type: 'error',
        message: 'Error pausing membership. Please try again.'
      });
    }
  };

  // Resume membership
  const resumeMembership = async () => {
    try {
      // Validate form data
      if (!resumeFormData.resumeDate) {
        setResumeNotification({
          show: true,
          type: 'error',
          message: 'Please select a resume date.'
        });
        return;
      }
      
      // Validate date format
      const resumeDate = new Date(resumeFormData.resumeDate);
      if (isNaN(resumeDate.getTime())) {
        setResumeNotification({
          show: true,
          type: 'error',
          message: 'Invalid date format. Please select a valid date.'
        });
        return;
      }

      // The resume date cannot precede the pause date it ends
      const activePauseDate = pauseSummary.activePause?.pauseDate;
      if (activePauseDate && resumeDate < new Date(new Date(activePauseDate).toDateString())) {
        setResumeNotification({
          show: true,
          type: 'error',
          message: `Resume date cannot be earlier than the pause date (${formatDate(activePauseDate)}).`
        });
        return;
      }

      console.log('▶️ Resuming membership for member:', member.id, 'from date:', resumeDate);
      
      const { resumeMembership: resumeMembershipFn } = await import('../firebase/database');
      const result = await resumeMembershipFn(tenantId, member.id, resumeDate.toISOString());
      
      if (result.success) {
        console.log('Membership resumed successfully:', result.resume);
        
        // Show success notification
        setResumeNotification({
          show: true,
          type: 'success',
          message: `Membership resumed successfully! Resume Date: ${resumeDate.toLocaleDateString()}, Next Billing: ${result.resume.nextBillingDate ? new Date(result.resume.nextBillingDate).toLocaleDateString() : 'Not set'}, New Billing Cycle: ${result.resume.newBillingCycle}. Monthly billing will resume next month.`
        });
        
        // Reset form and close modal after delay
        setTimeout(() => {
          setResumeFormData({
            resumeDate: new Date().toISOString().split('T')[0]
          });
          setShowResumeModal(false);
          setResumeNotification({ show: false, type: '', message: '' });
        }, 3000);
        
        // Refresh member data to show updated status
        await refreshMemberData();
      } else {
        console.log('Failed to resume membership:', result.message);
        setResumeNotification({
          show: true,
          type: 'error',
          message: `Failed to resume membership: ${result.message}`
        });
      }
    } catch (err) {
      console.error('Error resuming membership:', err);
      setResumeNotification({
        show: true,
        type: 'error',
        message: 'Error resuming membership. Please try again.'
      });
    }
  };

  // Form input handlers
  const handlePauseInputChange = (e) => {
    const { name, value } = e.target;
    setPauseFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleResumeInputChange = (e) => {
    const { name, value } = e.target;
    setResumeFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Open pause modal
  const openPauseModal = () => {
    setPauseFormData({
      reason: '',
      pauseDate: new Date().toISOString().split('T')[0]
    });
    setShowPauseModal(true);
  };

  // Open resume modal
  const openResumeModal = () => {
    setResumeFormData({
      resumeDate: new Date().toISOString().split('T')[0]
    });
    setShowResumeModal(true);
  };

  // Member details can be edited in any membership state
  const openEditForm = () => {
    setShowEditForm(true);
  };

  // Get pause information
  // eslint-disable-next-line no-unused-vars
  const getPauseInfo = async () => {
    try {
      console.log('⏸️ Getting pause info for member:', member.id);
      
      const { getMembershipPauseInfo } = await import('../firebase/database');
      const result = await getMembershipPauseInfo(tenantId, member.id);
      
      if (result.success) {
        console.log('Pause info:', result.pauseInfo);
        const pauseInfo = result.pauseInfo;
        
        if (pauseInfo.isPaused) {
          // Calculate pause duration
          const pauseStart = new Date(pauseInfo.pauseStartDate);
          const now = new Date();
          const pauseDuration = Math.ceil((now - pauseStart) / (1000 * 60 * 60 * 24));
          
          const pauseDetails = `Membership Status: PAUSED

Reason: ${pauseInfo.pauseReason}
Pause Start: ${pauseStart.toLocaleDateString()}
Pause Month: ${pauseInfo.pauseMonth}
Pause Duration: ${pauseDuration} days
Last Active: ${pauseInfo.lastActiveDate ? new Date(pauseInfo.lastActiveDate).toLocaleDateString() : 'Not recorded'}

No fees will be charged while paused.`;
          
          console.log('Pause Details:', pauseDetails);
          // You can display this information in a more user-friendly way if needed
        } else {
          const activeDetails = `Membership Status: ACTIVE

Next Billing: ${pauseInfo.nextBillingDate ? new Date(pauseInfo.nextBillingDate).toLocaleDateString() : 'Not set'}
Last Billing: ${pauseInfo.lastBillingDate ? new Date(pauseInfo.lastBillingDate).toLocaleDateString() : 'Not set'}
Billing Cycle: ${member.billingCycle || 1}

Membership is active and billing normally.`;
          
          console.log('Active Details:', activeDetails);
          // You can display this information in a more user-friendly way if needed
        }
      } else {
        console.log('Failed to get pause info:', result.message);
      }
    } catch (err) {
      console.error('Error getting pause info:', err);
    }
  };

  // Helper functions - moved here to ensure member exists
  const getPaymentStatusColor = (nextDueDate) => {
    if (!nextDueDate) return '#6b7280';
    const daysUntilDue = getDaysUntilDue(nextDueDate);
    if (daysUntilDue < 0) return '#dc2626'; // Overdue
    if (daysUntilDue <= 7) return '#f59e0b'; // Due soon
    return '#10b981'; // Good standing
  };

  const getPaymentStatusText = (nextDueDate) => {
    if (!nextDueDate) return 'Not Set';
    const daysUntilDue = getDaysUntilDue(nextDueDate);
    if (daysUntilDue < 0) return 'OVERDUE';
    if (daysUntilDue <= 7) return 'DUE SOON';
    return 'GOOD STANDING';
  };

  const getDaysUntilDue = (nextDueDate) => {
    const today = new Date();
    const dueDate = new Date(nextDueDate);
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getMemberSince = () => {
    // Always fall back to the original registration date so this never reads N/A
    const since =
      member.membershipStartDate || member.createdAt || originalRegistrationDate;
    const sinceDate = toDate(since);

    if (!sinceDate) return 'N/A';

    return sinceDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getNextRenewal = () => {
    if (member.nextDueDate) {
      return new Date(member.nextDueDate).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    }
    return 'N/A';
  };

  return (
    <div className="member-details-container">
      {/* Header */}
      <div className="member-details-header">
        <div className="member-header-info">
          <h1>{member.fullName}</h1>
          <p className="member-id">ID: {member.membershipId}</p>
          <div className="member-status">
            <span
              className="status-badge"
              style={{ backgroundColor: paused ? '#6b7280' : getPaymentStatusColor(member.nextDueDate) }}
            >
              {paused ? 'PAUSED' : getPaymentStatusText(member.nextDueDate)}
            </span>
            {/* Icon + history of every pause/resume event */}
            <PauseHistoryBadge member={member} />
          </div>
        </div>
      </div>
 
      {/* Top Row - Key Cards */}
      <div className="top-row-cards">
        {/* Personal Information Card */}
        <div className="info-card personal-info-card">
          <h3>👤 Personal Information</h3>
          <div className="info-grid-compact">
            <div className="info-item-compact">
              <span className="label-compact">FULL NAME</span>
              <span className="value-compact highlight">{member.fullName || 'N/A'}</span>
            </div>
            <div className="info-item-compact">
              <span className="label-compact">EMAIL</span>
              <span className="value-compact">{member.email || 'N/A'}</span>
            </div>
            <div className="info-item-compact">
              <span className="label-compact">
                {billingAnchorMoved ? 'ORIGINAL REGISTRATION DATE' : 'REGISTRATION DATE'}
              </span>
              <span className="value-compact">{formatDate(originalRegistrationDate)}</span>
            </div>
            {billingAnchorMoved && (
              <div className="info-item-compact">
                <span className="label-compact">NEW REGISTRATION DATE (RESUMED)</span>
                <span
                  className="value-compact highlight"
                  title="Fees are calculated from this date since the membership was resumed"
                >
                  ▶️ {formatDate(effectiveRegistrationDate)}
                </span>
              </div>
            )}
            <div className="info-item-compact">
              <span className="label-compact">MEMBER SINCE</span>
              <span className="value-compact">{getMemberSince()}</span>
            </div>
          </div>
        </div>
 
        {/* Membership Details Card */}
        <div className="info-card membership-details-card">
          <h3>🎯 Membership Details</h3>
          <div className="info-grid-compact">
            <div className="info-item-compact">
              <span className="label-compact">MEMBERSHIP LEVEL</span>
              <span className="value-compact">{member.membershipLevel || 'N/A'}</span>
            </div>
            <div className="info-item-compact">
              <span className="label-compact">DURATION</span>
              <span className="value-compact">{member.membershipDuration || 'N/A'}</span>
            </div>
            <div className="info-item-compact">
              <span className="label-compact">NEXT RENEWAL</span>
              <span className="value-compact">{getNextRenewal()}</span>
            </div>
          </div>
          
        </div>
 
        {/* Payment Status Card */}
        <div className="info-card payment-status-card-new">
          <h3>💳 Payment Status</h3>
          <div className="payment-status-content-new">
            <div className="status-badge-large-new">
              <span className="status-text-new">
                {paused ? 'PAUSED' : getPaymentStatusText(member.nextDueDate)}
              </span>
            </div>
            <div className="payment-amount">
              <span className="amount-value">₹{totalFee.toLocaleString()}</span>
              <span className="amount-label">
                {feeSummary.durationMonths === 1
                  ? 'Monthly Subscription'
                  : `Fee per ${feeSummary.durationMonths} months`}
              </span>
            </div>
            {balanceDue > 0 && (
              <div className="balance-due-display">
                <span className="balance-due-label">
                  {paused ? `Outstanding as of ${formatDate(feeSummary.pauseDate)}` : 'Remaining Balance'}
                </span>
                <span className="balance-due-amount outstanding">₹{balanceDue.toLocaleString()}</span>
              </div>
            )}
            
          </div>
        </div>
      </div>

      {/* Membership Management Section */}
      <div className="membership-management-section" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        border: '2px solid #e2e8f0',
        borderRadius: '16px',
        marginBottom: '2rem',
        padding: '1.5rem'
      }}>
        <h3 style={{ margin: '0 0 1.5rem 0', color: '#1f2937', fontSize: '1.5rem', fontWeight: '700' }}>
          ⚙️ Membership Management
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          {!paused ? (
            <button 
              onClick={openPauseModal}
              style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                color: 'white',
                border: 'none',
                padding: '1rem 1.5rem',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                boxShadow: '0 4px 6px rgba(220, 38, 38, 0.2)',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 12px rgba(220, 38, 38, 0.3)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.2)';
              }}
            >
              ⏸️ Pause Membership
            </button>
          ) : (
            <button 
              onClick={openResumeModal}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                padding: '1rem 1.5rem',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                boxShadow: '0 4px 6px rgba(16, 185, 129, 0.2)',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 12px rgba(16, 185, 129, 0.3)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 6px rgba(16, 185, 129, 0.2)';
              }}
            >
              ▶️ Resume Membership
            </button>
          )}
          
          <button 
            onClick={() => handleRecordPayment(member)}
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
              border: 'none',
              padding: '1rem 1.5rem',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 6px rgba(59, 130, 246, 0.2)',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 12px rgba(59, 130, 246, 0.3)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 6px rgba(59, 130, 246, 0.2)';
            }}
          >
            💰 Record Payment
          </button>

          {/* Editing stays available whether the membership is active, paused or resumed */}
          <button
            onClick={openEditForm}
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
              color: 'white',
              border: 'none',
              padding: '1rem 1.5rem',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 6px rgba(99, 102, 241, 0.2)',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 12px rgba(99, 102, 241, 0.3)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 6px rgba(99, 102, 241, 0.2)';
            }}
          >
            ✏️ Edit Member Details
          </button>

          {/* Permanent deletion - confirmed in a dedicated dialog */}
          <button
            onClick={() => setShowDeleteDialog(true)}
            style={{
              background: '#ffffff',
              color: '#b91c1c',
              border: '2px solid #fecaca',
              padding: '1rem 1.5rem',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#fef2f2';
              e.target.style.borderColor = '#fca5a5';
            }}
            onMouseOut={(e) => {
              e.target.style.background = '#ffffff';
              e.target.style.borderColor = '#fecaca';
            }}
          >
            🗑️ Delete Member
          </button>
        </div>

        {paused && (
          <p style={{ margin: '1rem 0 0 0', color: '#7c2d12', fontSize: '0.9rem' }}>
            ⏸️ Fee calculation stopped on <strong>{formatDate(feeSummary.pauseDate)}</strong>. Member
            details can still be edited while paused.
          </p>
        )}
      </div>

      {/* Balance Summary Banner */}
      <div className="balance-summary-banner">
        <div className="balance-banner-content">
          <div className="balance-banner-item">
            <span className="banner-label">
              {feeSummary.durationMonths === 1 ? 'Monthly Fee' : 'Fee Per Cycle'}
            </span>
            <span className="banner-value total">₹{totalFee.toLocaleString()}</span>
          </div>
          <div className="balance-banner-item">
            <span className="banner-label">
              {paused ? `Charged Up To ${formatDate(feeSummary.pauseDate)}` : 'Charged To Date'}
            </span>
            <span className="banner-value total">
              ₹{totalCharged.toLocaleString()}
              <small style={{ display: 'block', fontSize: '0.7rem', fontWeight: 500, opacity: 0.75 }}>
                {feeSummary.periodsCharged} billing cycle{feeSummary.periodsCharged === 1 ? '' : 's'}
              </small>
            </span>
          </div>
          <div className="balance-banner-item">
            <span className="banner-label">Amount Paid</span>
            <span className="banner-value paid">₹{totalPaid.toLocaleString()}</span>
          </div>
          <div className="balance-banner-item">
            <span className="banner-label">Balance Due</span>
            <span className={`banner-value ${balanceDue > 0 ? 'outstanding' : 'paid'}`}>
              ₹{balanceDue.toLocaleString()}
            </span>
          </div>
          <div className="balance-banner-item">
            <span className="banner-label">Status</span>
            <span className={`banner-status ${isFullyPaid ? 'full' : 'partial'}`}>
              {isFullyPaid ? '✅ Fully Paid' : '⚠️ Partial Payment'}
            </span>
          </div>
        </div>
        <p style={{
          margin: '0.75rem 1rem 0 1rem',
          fontSize: '0.8rem',
          color: '#475569'
        }}>
          {paused
            ? `⏸️ Fees stopped accruing on ${formatDate(feeSummary.pauseDate)}. Nothing further is charged until the membership is resumed.`
            : `▶️ Fees accrue from ${formatDate(feeSummary.effectiveRegistrationDate)}. Next cycle starts ${formatDate(feeSummary.nextChargeDate)}.`}
          {feeSummary.carriedChargedPeriods > 0 &&
            ` Includes ₹${feeSummary.carriedChargedAmount.toLocaleString()} carried over from ${feeSummary.carriedChargedPeriods} cycle(s) billed before the pause.`}
        </p>
      </div>

      {/* Membership Status Section */}
      <div className="membership-status-section" style={{
        background: '#f8fafc',
        border: '2px solid #e2e8f0',
        borderRadius: '16px',
        marginBottom: '2rem',
        padding: '1.5rem'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937' }}>📋 Membership Status</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem' }}>
              MEMBERSHIP STATUS
            </div>
            <div style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: paused ? '#dc2626' : '#059669'
            }}>
              {paused ? '⏸️ PAUSED' : '✅ ACTIVE'}
            </div>
          </div>

          <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem' }}>
              BILLING CYCLE
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1f2937' }}>
              Month {(member.billingCycle || 1)}
            </div>
          </div>
          
          <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem' }}>
              NEXT BILLING
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1f2937' }}>
              {member.nextBillingDate ? new Date(member.nextBillingDate).toLocaleDateString() : 'Not Set'}
            </div>
          </div>
          
          {paused && pauseSummary.activePause && (
            <>
              <div style={{ background: '#fef2f2', padding: '1rem', borderRadius: '8px', textAlign: 'center', border: '1px solid #fecaca' }}>
                <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#991b1b', marginBottom: '0.5rem' }}>
                  PAUSE START DATE
                </div>
                <div style={{ fontSize: '1rem', fontWeight: '600', color: '#dc2626' }}>
                  {formatDate(pauseSummary.activePause.pauseDate, 'Not Set')}
                </div>
              </div>

              <div style={{ background: '#fef2f2', padding: '1rem', borderRadius: '8px', textAlign: 'center', border: '1px solid #fecaca' }}>
                <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#991b1b', marginBottom: '0.5rem' }}>
                  PAUSE REASON
                </div>
                <div style={{ fontSize: '1rem', fontWeight: '600', color: '#dc2626' }}>
                  {pauseSummary.activePause.reason || 'No reason specified'}
                </div>
              </div>

              <div style={{ background: '#fef2f2', padding: '1rem', borderRadius: '8px', textAlign: 'center', border: '1px solid #fecaca' }}>
                <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#991b1b', marginBottom: '0.5rem' }}>
                  PAUSE DURATION
                </div>
                <div style={{ fontSize: '1rem', fontWeight: '600', color: '#dc2626' }}>
                  {pauseSummary.activePause.durationDays} days
                </div>
              </div>
            </>
          )}

          {member.resumeDate && (
            <div style={{ background: '#dcfce7', padding: '1rem', borderRadius: '8px', textAlign: 'center', border: '1px solid #bbf7d0' }}>
              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#166534', marginBottom: '0.5rem' }}>
                LAST RESUMED
              </div>
              <div style={{ fontSize: '1rem', fontWeight: '600', color: '#059669' }}>
                {formatDate(member.resumeDate)}
              </div>
            </div>
          )}
        </div>

        {/* Full pause / resume history */}
        {pauseSummary.hasBeenPaused && (
          <div style={{ marginTop: '1.5rem' }}>
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '0.5rem',
              marginBottom: '0.75rem'
            }}>
              <h4 style={{ margin: 0, color: '#1f2937' }}>⏸️ Pause &amp; Resume History</h4>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                {pauseSummary.totalPauses} pause{pauseSummary.totalPauses === 1 ? '' : 's'} ·{' '}
                {pauseSummary.totalPausedDays} day{pauseSummary.totalPausedDays === 1 ? '' : 's'} paused in total
              </span>
            </div>

            <div className="payment-history-table">
              <table>
                <thead>
                  <tr>
                    <th>Paused On</th>
                    <th>Resumed On</th>
                    <th>Duration</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th>Recorded By</th>
                  </tr>
                </thead>
                <tbody>
                  {pauseSummary.history.map((entry) => (
                    <tr key={entry.id}>
                      <td>{formatDate(entry.pauseDate)}</td>
                      <td>{entry.resumeDate ? formatDate(entry.resumeDate) : '—'}</td>
                      <td>
                        {entry.durationDays} day{entry.durationDays === 1 ? '' : 's'}
                        {entry.status === 'active' ? ' (ongoing)' : ''}
                      </td>
                      <td>{entry.reason}</td>
                      <td>
                        <span className={`status-badge ${entry.status === 'active' ? 'paused' : 'completed'}`}>
                          {entry.status === 'active' ? 'Ongoing' : 'Completed'}
                        </span>
                      </td>
                      <td>
                        {entry.pausedBy || '—'}
                        {entry.resumedBy ? ` / ${entry.resumedBy}` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      
      {/* Main Content - New Layout */}
     
   

      {/* Weight check-ins over time */}
      <WeightTracker
        tenantId={tenantId}
        member={member}
        onRecorded={refreshMemberData}
      />

      {/* Recent changes made to this member's details */}
      <MemberActivityLog
        tenantId={tenantId}
        memberId={member.id}
        refreshKey={activityRefreshKey}
      />

      {/* Payment History Section */}
      <div className="payment-history-section">
        <h3>📊 Payment History</h3>
        {isLoading ? (
          <div className="loading">Loading payment history...</div>
        ) : paymentHistory.length > 0 ? (
          <div className="payment-history-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th>Notes</th>
                  <th>Ref ID</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((payment, index) => (
                  <tr key={payment.id || index}>
                    <td>{payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : '-'}</td>
                    <td>₹{payment.amount?.toLocaleString?.() || payment.amount || 0}</td>
                    <td>{payment.paymentMethod || '-'}</td>
                    <td>
                      <span className={`status-badge ${payment.status || 'completed'}`}>
                        {payment.status || 'completed'}
                      </span>
                    </td>
                    <td>{payment.notes || '-'}</td>
                    <td>{payment.id || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="no-payments">
            <p>No payment history available.</p>
          </div>
        )}
      </div>

      {/* Modal Forms */}
      {showPaymentForm && (
        <div className="modal-overlay" onClick={(e) => {
          console.log('Modal overlay clicked');
          if (e.target === e.currentTarget) {
            closePaymentForm();
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Record Payment - {member.fullName}</h3>
              <button className="modal-close" onClick={() => {
                console.log('Payment form close button clicked');
                closePaymentForm();
              }}>×</button>
            </div>
            <PaymentForm 
              tenantId={tenantId}
              member={member} 
              onSuccess={() => {
                console.log('Payment form success callback');
                closePaymentForm();
                // Refresh member data to show updated balance
                refreshMemberData();
              }}
              onClose={() => {
                console.log('Payment form close callback');
                closePaymentForm();
              }}
            />
          </div>
        </div>
      )}

      
      

      {/* Delete confirmation - returns to the members list on success */}
      {showDeleteDialog && (
        <DeleteMemberDialog
          tenantId={tenantId}
          member={member}
          onCancel={() => setShowDeleteDialog(false)}
          onDeleted={(result) => {
            setShowDeleteDialog(false);
            navigate('/dashboard', {
              state: { activeTab: 'members', notice: result.message }
            });
          }}
        />
      )}

      {/* Edit Member Details - available in every membership state */}
      {showEditForm && (
        <MembershipForm
          tenantId={tenantId}
          member={member}
          onClose={() => setShowEditForm(false)}
          onSuccess={() => {
            setShowEditForm(false);
            refreshMemberData();
          }}
        />
      )}

      {/* Pause Membership Modal */}
      {showPauseModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowPauseModal(false);
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Pause Membership - {member.fullName}</h3>
              <button className="modal-close" onClick={() => setShowPauseModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {/* Notification Display */}
              {pauseNotification.show && (
                <div className={`notification ${pauseNotification.type}`} style={{
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  borderRadius: '8px',
                  border: '1px solid',
                  backgroundColor: pauseNotification.type === 'success' ? '#dcfce7' : '#fef2f2',
                  borderColor: pauseNotification.type === 'success' ? '#bbf7d0' : '#fecaca',
                  color: pauseNotification.type === 'success' ? '#166534' : '#991b1b'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>{pauseNotification.type === 'success' ? '✅' : '❌'}</span>
                    <span style={{ fontWeight: '500' }}>{pauseNotification.message}</span>
                  </div>
                </div>
              )}
              
              <form onSubmit={(e) => {
                e.preventDefault();
                pauseMembership();
              }}>
                <div className="form-group">
                  <label htmlFor="pauseReason">Reason for Pausing:</label>
                  <textarea
                    id="pauseReason"
                    name="reason"
                    value={pauseFormData.reason}
                    onChange={handlePauseInputChange}
                    rows="3"
                    required
                    placeholder="Enter reason for pausing membership (e.g., No attendance, Travel, Medical, etc.)"
                  ></textarea>
                </div>
                <div className="form-group">
                  <label htmlFor="pauseDate">Pause Start Date:</label>
                  <input
                    type="date"
                    id="pauseDate"
                    name="pauseDate"
                    value={pauseFormData.pauseDate}
                    onChange={handlePauseInputChange}
                    min={effectiveRegistrationDate ? new Date(effectiveRegistrationDate).toISOString().split('T')[0] : undefined}
                    required
                  />
                  <small style={{ color: '#64748b' }}>
                    Fees stop accruing on this date. Currently charged to date: ₹
                    {totalCharged.toLocaleString()} ({feeSummary.periodsCharged} cycle
                    {feeSummary.periodsCharged === 1 ? '' : 's'}).
                  </small>
                </div>
                <div className="modal-footer">
                  <button type="button" className="modal-btn-secondary" onClick={() => setShowPauseModal(false)}>Cancel</button>
                  <button type="submit" className="modal-btn-primary">Confirm Pause</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Resume Membership Modal */}
      {showResumeModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowResumeModal(false);
          }
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Resume Membership - {member.fullName}</h3>
              <button className="modal-close" onClick={() => setShowResumeModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {/* Notification Display */}
              {resumeNotification.show && (
                <div className={`notification ${resumeNotification.type}`} style={{
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  borderRadius: '8px',
                  border: '1px solid',
                  backgroundColor: resumeNotification.type === 'success' ? '#dcfce7' : '#fef2f2',
                  borderColor: resumeNotification.type === 'success' ? '#bbf7d0' : '#fecaca',
                  color: resumeNotification.type === 'success' ? '#166534' : '#991b1b'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>{resumeNotification.type === 'success' ? '✅' : '❌'}</span>
                    <span style={{ fontWeight: '500' }}>{resumeNotification.message}</span>
                  </div>
                </div>
              )}
              
              <form onSubmit={(e) => {
                e.preventDefault();
                resumeMembership();
              }}>
                <div className="form-group">
                  <label htmlFor="resumeDate">Resume Date:</label>
                  <input
                    type="date"
                    id="resumeDate"
                    name="resumeDate"
                    value={resumeFormData.resumeDate}
                    onChange={handleResumeInputChange}
                    min={pauseSummary.activePause?.pauseDate
                      ? new Date(pauseSummary.activePause.pauseDate).toISOString().split('T')[0]
                      : undefined}
                    required
                  />
                  <small style={{ color: '#64748b' }}>
                    This date becomes the new billing start date. The original registration date
                    ({formatDate(originalRegistrationDate)}) is kept in the member's history.
                  </small>
                </div>
                <div className="modal-footer">
                  <button type="button" className="modal-btn-secondary" onClick={() => setShowResumeModal(false)}>Cancel</button>
                  <button type="submit" className="modal-btn-primary">Confirm Resume</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberDetails;
